import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import {
  ArrowDownLeft, ArrowUpRight, Wallet, TrendingUp, Repeat, Trophy, Landmark, Loader2,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const PIE_COLORS = ["#4338CA", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#64748B"];

const fileUrl = (u) => (u ? `${process.env.REACT_APP_BACKEND_URL}${u}` : null);

export default function Dashboard() {
  const { companyId, currency, company } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    setLoading(true);
    api.get(`/companies/${companyId}/dashboard`).then(({ data }) => setData(data))
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, [companyId]);

  if (!companyId)
    return <Empty title="No company selected" msg="Create a company profile to get started." />;
  if (loading)
    return <div className="grid place-items-center h-96"><Loader2 className="animate-spin text-indigo-600" size={32} /></div>;
  if (!data)
    return <Empty title="Couldn't load dashboard" msg="Please refresh or try again shortly." />;

  const fmt = (v) => formatMoney(v, currency);
  const fmtC = (v) => formatMoney(v, currency, true);

  const kpis = [
    { label: "Money In", value: data.total_in, icon: ArrowDownLeft, color: "emerald", tint: "bg-emerald-50 text-emerald-600" },
    { label: "Money Out", value: data.total_out, icon: ArrowUpRight, color: "rose", tint: "bg-rose-50 text-rose-600" },
    { label: "Current Balance", value: data.balance, icon: Wallet, color: "indigo", tint: "bg-indigo-50 text-indigo-600" },
    { label: "Net Flow", value: data.net, icon: TrendingUp, color: "violet", tint: "bg-violet-50 text-violet-600" },
  ];

  return (
    <div className="space-y-6 animate-fade-up" data-testid="dashboard-page">
      <div className="flex items-center gap-3">
        {company?.logo_url && (
          <img src={fileUrl(company.logo_url)} alt="" className="w-12 h-12 rounded-xl object-cover border border-slate-200" data-testid="dashboard-logo" />
        )}
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">{company?.name}</h1>
          <p className="text-slate-500 text-sm mt-0.5">Here's how much came in, went out, and where you stand.</p>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, "-")}`}
            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${k.tint}`}>
              <k.icon size={20} />
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mt-4">{k.label}</p>
            <p className={`text-xl md:text-2xl font-heading font-extrabold mt-1 ${
              k.color === "emerald" ? "text-emerald-600" : k.color === "rose" ? "text-rose-600" : "text-slate-900"}`}>
              {fmt(k.value)}
            </p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card title="Monthly Cash Flow" className="lg:col-span-2" testid="cashflow-chart">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={data.monthly} margin={{ left: -10, right: 10, top: 10 }}>
              <defs>
                <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={fmtC} width={70} />
              <Tooltip content={<ChartTip fmt={fmt} />} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 13 }} />
              <Area type="monotone" dataKey="in" name="Money In" stroke="#10B981" strokeWidth={2.5} fill="url(#gIn)" />
              <Area type="monotone" dataKey="out" name="Money Out" stroke="#EF4444" strokeWidth={2.5} fill="url(#gOut)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Spending by Category" testid="category-chart">
          {data.categories.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data.categories.slice(0, 8)} dataKey="value" nameKey="name" cx="50%" cy="45%"
                  innerRadius={55} outerRadius={85} paddingAngle={2}>
                  {data.categories.slice(0, 8).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<ChartTip fmt={fmt} />} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <NoData />}
        </Card>
      </div>

      {/* Income vs Expense + Accounts */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card title="Income vs Expenses" className="lg:col-span-2" testid="income-expense-chart">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.monthly} margin={{ left: -10, right: 10, top: 10 }} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={fmtC} width={70} />
              <Tooltip content={<ChartTip fmt={fmt} />} cursor={{ fill: "#F8FAFC" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 13 }} />
              <Bar dataKey="in" name="Income" fill="#10B981" radius={[6, 6, 0, 0]} />
              <Bar dataKey="out" name="Expenses" fill="#4338CA" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Accounts" testid="accounts-breakdown">
          <div className="space-y-3">
            {data.accounts.length ? data.accounts.map((a, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <div className="w-9 h-9 rounded-lg bg-indigo-100 text-indigo-600 grid place-items-center"><Landmark size={17} /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{a.name}</p>
                  <p className="text-xs text-slate-400">{a.bank}</p>
                </div>
                <p className="text-sm font-bold text-slate-900">{fmtC(a.balance)}</p>
              </div>
            )) : <NoData />}
          </div>
        </Card>
      </div>

      {/* Largest + Recurring */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Largest Transactions" icon={Trophy} testid="largest-txns">
          <div className="space-y-2">
            {data.largest.length ? data.largest.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{t.description}</p>
                  <p className="text-xs text-slate-400">{formatDate(t.date)} · {t.category}</p>
                </div>
                <span className={`text-sm font-bold ${t.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                  {t.type === "credit" ? "+" : "-"}{fmt(t.amount)}
                </span>
              </div>
            )) : <NoData />}
          </div>
        </Card>

        <Card title="Recurring Payments" icon={Repeat} testid="recurring-txns">
          <div className="space-y-2">
            {data.recurring.length ? data.recurring.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{r.description}</p>
                  <p className="text-xs text-slate-400">{r.count}× · avg</p>
                </div>
                <span className={`text-sm font-bold ${r.type === "credit" ? "text-emerald-600" : "text-slate-700"}`}>{fmt(r.avg_amount)}</span>
              </div>
            )) : <NoData />}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`bg-white rounded-2xl border border-slate-200 p-5 md:p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)] ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={18} className="text-indigo-600" />}
        <h3 className="font-heading font-bold text-slate-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function ChartTip({ active, payload, label, fmt }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-lg p-3 text-sm">
      {label && <p className="font-semibold text-slate-700 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} className="flex items-center gap-2 text-slate-600">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.color || p.payload?.fill }} />
          {p.name}: <span className="font-bold text-slate-900">{fmt(p.value)}</span>
        </p>
      ))}
    </div>
  );
}

const NoData = () => <div className="h-40 grid place-items-center text-slate-400 text-sm">No data yet</div>;

function Empty({ title, msg }) {
  return (
    <div className="h-96 grid place-items-center text-center">
      <div>
        <h2 className="text-xl font-heading font-bold text-slate-800">{title}</h2>
        <p className="text-slate-500 mt-1">{msg}</p>
      </div>
    </div>
  );
}
